import React from 'react';
import _sumBy from 'lodash/sumBy';

import { formatDollarsStandard, locale } from '@trqk/utils';

import { IconInfoCircle, Tooltip } from '../../../..';
import { legendSizes } from '../constants';

interface TotalRoyaltiesSummaryProps {
    /**
     * Data used populate the table in the QuarterlyRunRateLegend
     */
    data: QRRLegendTableItem[];
    /**
     * The subscription_royalty_capacity in the user's current payment plan
     */
    royaltyCapacity: Maybe<number>;
    /**
     * When true, the word "Unlimited" will be used as the label for royalty capacity.
     */
    unlimitedRoyalties: boolean;
}

/**
 * Renders a summary row about the data in the QuarterlyRunRateLegend, including the totals for both
 * the `totalEarnings` field and the `mostRecentEarnings` field. Also displays the total
 * royalties available in the customer's payment plan.
 * @param props - {@link TotalRoyaltiesSummaryProps}
 */
export const QuarterlyRunRateLegendSummary = ({
    data,
    royaltyCapacity,
    unlimitedRoyalties,
}: TotalRoyaltiesSummaryProps): JSX.Element => {
    const mostRecentEarningsSum = _sumBy(data, 'mostRecentEarnings');
    const totalEarningsSum = _sumBy(data, 'totalEarnings');
    const mostRecentEarningsSumFormatted = formatDollarsStandard(
        mostRecentEarningsSum,
    );

    return (
        <div className="text-xs mt-1 flex flex-col">
            <div className="flex justify-between">
                <div
                    className="px-1 font-bold"
                    style={{
                        width: legendSizes.columnWidths.entityName,
                    }}
                >
                    {locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES.TOTAL}
                </div>
                <div
                    className="px-1 text-right"
                    style={{
                        width: legendSizes.columnWidths.totalEarnings,
                    }}
                >
                    {formatDollarsStandard(totalEarningsSum)}
                </div>
                <Tooltip
                    background="light"
                    message={
                        <div
                            className="grid mt-3"
                            style={{ gridTemplateColumns: '32px auto' }}
                        >
                            <IconInfoCircle
                                addClasses="ml-1"
                                fillColor="primary-500"
                                width={20}
                            />
                            <div className="text-left text-tertiary-400 mr-3 mb-3 font-normal space-y-4">
                                <p className="font-medium">
                                    {
                                        locale.enUS.MANAGE_STATEMENTS
                                            .TOTAL_ROYALTIES
                                            .QRR_CALCULATION_INFO.LINE_1
                                    }{' '}
                                    <span className="font-bold">
                                        {mostRecentEarningsSumFormatted}
                                    </span>
                                    .
                                </p>
                                <p className="font-medium">
                                    {
                                        locale.enUS.MANAGE_STATEMENTS
                                            .TOTAL_ROYALTIES
                                            .QRR_CALCULATION_INFO.LINE_2
                                    }
                                </p>
                            </div>
                        </div>
                    }
                    minWidth={400}
                    maxWidth={400}
                    position="left"
                >
                    <div
                        className="px-1 font-bold text-right text-primary-500"
                        style={{
                            width: legendSizes.columnWidths.mostRecentEarnings,
                        }}
                    >
                        <IconInfoCircle
                            fillColor="primary-500"
                            width={16}
                            addClasses="mr-1"
                        />
                        {mostRecentEarningsSumFormatted}
                    </div>
                </Tooltip>
            </div>
            <div className="flex justify-between">
                <div className="px-1">
                    {
                        locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                            .AVAILABLE_IN_PLAN
                    }
                </div>
                <div className="px-1 text-right" />
                <div className="px-1 text-right">
                    {!unlimitedRoyalties && (
                        <>{formatDollarsStandard(royaltyCapacity ?? 0)}</>
                    )}
                    {unlimitedRoyalties && (
                        <>
                            {
                                locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                                    .UNLIMITED
                            }
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
